import { createUuid } from '@notifi/uuid/browser';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Plus, Loader2, FileText, Lock, ScrollText, StickyNote, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useLocationPrefill } from '@/hooks/locations/useLocationPrefill';
import { Button } from '@/components/atoms/button';
import { Textarea } from '@/components/atoms/textarea';
import { CreateFromTemplatePicker, FormPageLayout } from '@/components/layouts';
import { FormSectionCard, FormSectionHeader } from '@/components/molecules';
import { useContact } from '@/hooks/contacts/useContact';
import { useEstimateMembershipInfo } from '@/hooks/estimates/useEstimateMembershipInfo';
import { useMembershipPlansByIds } from '@/hooks/pricebook/useMembershipPlans';
import { useCreateProposal } from '@/hooks/proposals/useCreateProposal';
import { useUpdateProposal } from '@/hooks/proposals/useUpdateProposal';
import { useProposal } from '@/hooks/proposals/useProposal';
import { useProposalTemplates } from '@/hooks/proposalTemplates/useProposalTemplates';
import { useBusinessUnitScopeSelection } from '@/components/organisms/business-units/useBusinessUnitScopeSelection';
import { isBusinessUnitId } from '@/lib/business-units/businessUnitScope';
import { useDebounce } from '@/hooks/useDebounce';
import { useProposalTemplate } from '@/hooks/proposalTemplates/useProposalTemplate';
import { useProposalAttachmentState } from '@/hooks/proposals/useProposalAttachmentState';
import { EstimateCustomerContentSection } from '@/components/organisms/estimates/EstimateCustomerContentSection';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import { JobCustomerContactsSection } from '@/components/organisms/jobs/JobCustomerContactsSection';
import { ProposalDetailsSection } from '@/components/organisms/proposals/ProposalDetailsSection';
import { ProposalOptionsSection } from '@/components/organisms/proposals/ProposalOptionsSection';
import { apiPost, operationsClient } from '@/lib/api';
import { proposalTemplateEndpoints } from '@/lib/api/endpoints';
import { sanitizePostalCodeInput } from '@/lib/postal-code';
import type { AddressPartsLike } from '@/lib/address-match';
import type { ContactAddress } from '@/types/contact';
import {
  formatContactAddress,
  getContactDisplayName,
  getPrimaryContactAddress,
} from '@/lib/contacts';
import { type OptionState } from '@/components/organisms/OptionCard';
import {
  EMPTY_TERMS_SELECTION,
  hasTermsSelection,
  readTermsSelection,
  resolveTermsSelection,
  seedDocumentTerms,
  type TermsSelection,
} from '@/lib/terms';
import { useJob } from '@/hooks/jobs/useJob';
import { useTermsTemplates } from '@/hooks/settings/useTermsTemplates';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';
import { assessProposalPaymentSchedules } from '@/components/organisms/proposals/support/proposal-payment-schedule';
import { proposalOptionDepositAmountOn } from '@/components/organisms/proposals/support/proposal-option-math';
import {
  MAX_PROPOSAL_OPTIONS,
  MIN_PROPOSAL_OPTIONS,
  buildProposalPayload,
  createEmptyProposalOption,
  createProposalOptionClientId,
  findProposalBillingAddressIndex,
  findProposalOptionBoundError,
  computeProposalOptionPreview,
  resolveOptionMembershipInfo,
  findProposalServiceAddressIndex,
  hydrateProposalOptions,
  proposalPickerCarry,
  proposalSaveBlockedReason,
  proposalSubmitLocked,
  resolveProposalBillingAddressIndex,
  resolveProposalServiceAddressIndex,
  sourceJobAddressPicks,
} from './_shared';
import { ProposalFormActions } from '@/components/organisms/proposals/ProposalFormActions';
import { SendConfirmModal } from '@/components/shared/SendConfirmModal';
import {
  useSendProposal,
  type ProposalSendApprovalMethod,
} from '@/hooks/proposals/useSendProposal';

/** A proposal page can take a drawn signature or a click — never an off-page answer. */
const PROPOSAL_APPROVAL_METHODS = ['SIGNATURE', 'ONLINE'] as const;

/**
 * One sentence for the disabled Save & Send button and for the click-time
 * refusal, so the two can never describe the same state differently.
 */
const SEND_BLOCKED_REASON =
  'This customer has no email or phone number, so there is nothing to send to.';

// ─── Route ───────────────────────────────────────────────────────────────────

// NOT-560 — loose contact shape accepted by both the picker (its ContactResult)
// and the useContact record, so one helper can prefill the customer fields.
interface ContactSelectionInput {
  _id?: string;
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  addresses?: ContactAddress[];
}

/** A job's address snapshot in the shape the address fields are filled from. */
function toContactAddress(address: AddressPartsLike): ContactAddress {
  return {
    street: address.street ?? address.street1 ?? '',
    street2: address.street2 ?? undefined,
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.zip ?? address.postalCode ?? '',
    country: address.country ?? undefined,
  };
}

type ProposalNewSearch = {
  template?: string;
  // NOT-560 — when a proposal is created *on* a SERVICE_CALL job, the job page
  // links here with the call id (threaded onto the proposal so the chain call →
  // proposal → estimate → job stays traceable) and its contact id (prefill).
  sourceJobId?: string;
  contactId?: string;
  locationId?: string;
  /**
   * The Business Unit chosen on the picker screen. It narrowed the proposal
   * templates offered there, and narrows the estimate templates each option can
   * import lines from. Optional — absent filters nothing.
   */
  businessUnitId?: string;
};

export const Route = createFileRoute('/_dashboard/dashboard/proposals/new')({
  component: NewProposalWrapper,
  validateSearch: (search: Record<string, unknown>): ProposalNewSearch => ({
    template: typeof search.template === 'string' ? search.template : undefined,
    sourceJobId: typeof search.sourceJobId === 'string' ? search.sourceJobId : undefined,
    contactId: typeof search.contactId === 'string' ? search.contactId : undefined,
    locationId: typeof search.locationId === 'string' ? search.locationId : undefined,
    businessUnitId: isBusinessUnitId(search.businessUnitId) ? search.businessUnitId : undefined,
  }),
});

// ─── Wrapper ─────────────────────────────────────────────────────────────────

/**
 * Exported so a spec can render the real screen. The route's own
 * `component` is code-split, so reaching it through `Route` in a test
 * suspends forever on a chunk vitest never resolves.
 */
export function NewProposalWrapper() {
  const search = Route.useSearch();

  if (search.template === 'scratch') {
    return (
      <ProposalFormPage
        sourceJobId={search.sourceJobId}
        prefillContactId={search.contactId}
        prefillLocationId={search.locationId}
        selectedBusinessUnitId={search.businessUnitId}
      />
    );
  }

  if (search.template) {
    return (
      <ProposalFormPage
        templateId={search.template}
        sourceJobId={search.sourceJobId}
        prefillContactId={search.contactId}
        prefillLocationId={search.locationId}
        selectedBusinessUnitId={search.businessUnitId}
      />
    );
  }

  // A job opens the picker rather than the builder, so its link and customer
  // sit in the URL while the operator chooses. The picker navigates to
  // this same route, which REPLACES the search — so they must travel with it.
  return (
    <TemplatePicker
      initialBusinessUnitId={search.businessUnitId}
      sourceJobId={search.sourceJobId}
      contactId={search.contactId}
      locationId={search.locationId}
    />
  );
}

// ─── Template Picker ──────────────────────────────────────────────────────────

function TemplatePicker({
  initialBusinessUnitId,
  sourceJobId,
  contactId,
  locationId,
}: {
  initialBusinessUnitId?: string;
  sourceJobId?: string;
  contactId?: string;
  locationId?: string;
}) {
  const router = useRouter();
  const [templateSearch, setTemplateSearch] = useState('');
  const [templatePage, setTemplatePage] = useState(1);
  const debouncedTemplateSearch = useDebounce(templateSearch.trim(), 300);
  const templateSearchIsSettling = templateSearch.trim() !== debouncedTemplateSearch;
  // Seed from the URL so a shared or bookmarked link naming a unit opens on it,
  // the way the New Estimate picker does. The route validated the SHAPE; the
  // hook is what drops an id the active-unit list turns out not to contain.
  const { businessUnitId, setBusinessUnitId, hasBusinessUnits } =
    useBusinessUnitScopeSelection(initialBusinessUnitId);
  const {
    data: templatesData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useProposalTemplates({
    search: debouncedTemplateSearch || undefined,
    // Undefined ⇒ the key is omitted and every template is listed.
    businessUnitId: businessUnitId ?? undefined,
    limit: 50,
    page: templatePage,
  });
  const templates = templatesData?.data ?? [];
  const carry = proposalPickerCarry({
    sourceJobId,
    contactId,
    locationId,
    businessUnitId,
  });

  return (
    <CreateFromTemplatePicker
      title="New Proposal"
      description="Choose how to start"
      // Back, not "go to the proposals list". A job opens this picker now, so a
      // fixed destination would strand the operator outside the job workspace
      // they started from, and arriving from the list this restores the filters
      // it was showing.
      //
      // Unconditional would be wrong the other way: `businessUnitId` rides in
      // the URL precisely so a link to this screen can be shared, and a pasted
      // one has nothing behind it to return to. `canGoBack` answers from the
      // router's own location index rather than window.history, so it is false
      // exactly in that case — and then the list is the only sensible home.
      onBack={() => {
        if (router.history.canGoBack()) {
          router.history.back();
          return;
        }
        void router.navigate({ to: '/dashboard/proposals', search: {} as never });
      }}
      businessUnitStep={
        hasBusinessUnits
          ? {
              value: businessUnitId,
              onChange: (value: string | null) => {
                // A narrower list renumbers: page 3 of "everything" is usually
                // past the end of one business unit's templates.
                setTemplatePage(1);
                setBusinessUnitId(value);
              },
              narrows: 'templates',
            }
          : undefined
      }
      scratchTitle="From Scratch"
      scratchDescription="Create a blank proposal with custom options"
      scratchIcon={Plus}
      scratchOptions={[
        {
          title: 'Blank Proposal',
          description: 'Start with 2 empty options, add your own items',
          onClick: () =>
            void router.navigate({
              to: '/dashboard/proposals/new',
              search: { template: 'scratch', ...carry } as never,
            }),
          icon: FileText,
          emphasis: 'primary',
        },
      ]}
      templateTitle="From Template"
      templateDescription="Start with saved options and line items"
      templateIcon={FileText}
      isLoadingTemplates={isLoading || templateSearchIsSettling}
      isSearchingTemplates={isFetching && !isLoading}
      templateEmptyMessage="No templates yet. Create one from the Templates section."
      templateSearchValue={templateSearch}
      onTemplateSearchChange={(value) => {
        setTemplateSearch(value);
        setTemplatePage(1);
      }}
      templateSearchPlaceholder="Search proposal templates..."
      templateNoResultsMessage="No proposal templates match your search."
      templateLoadError={
        isError
          ? {
              message: 'Couldn’t load proposal templates.',
              onRetry: () => void refetch(),
            }
          : undefined
      }
      templatePagination={
        templatesData
          ? {
              page: templatesData.page,
              totalPages: templatesData.totalPages,
              total: templatesData.total,
              onPageChange: setTemplatePage,
            }
          : undefined
      }
      templateOptions={templates.map((template) => {
        const options =
          (
            template as unknown as Record<string, unknown> & {
              options?: Record<string, unknown>[];
            }
          ).options ?? [];
        const totalItems = options.reduce(
          (sum: number, option: Record<string, unknown>) =>
            sum + ((option.lineItems as unknown[])?.length ?? 0),
          0,
        );

        return {
          id: template.id,
          title: template.name,
          description: `${template.description ? `${template.description} · ` : ''}${options.length} option${options.length !== 1 ? 's' : ''} · ${totalItems} item${totalItems !== 1 ? 's' : ''}`,
          onClick: () =>
            void router.navigate({
              to: '/dashboard/proposals/new',
              search: { template: template.id, ...carry } as never,
            }),
        };
      })}
    />
  );
}

function AddChip({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      className="h-8 rounded-full px-3 text-xs"
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      {label}
    </Button>
  );
}

function SituationalPanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Proposal Form ────────────────────────────────────────────────────────────

export function ProposalFormPage({
  editId,
  templateId,
  // NOT-560 — service-call context passed from the job page. `sourceJobId` is
  // threaded onto the created proposal; `prefillContactId` prefills the customer.
  sourceJobId,
  prefillContactId,
  prefillLocationId,
  selectedBusinessUnitId,
}: {
  editId?: string;
  templateId?: string;
  sourceJobId?: string;
  prefillContactId?: string;
  prefillLocationId?: string;
  /**
   * The Business Unit picked in the create flow. Used only to narrow the
   * estimate templates each option may import lines from — undefined means no
   * narrowing at all.
   */
  selectedBusinessUnitId?: string;
}) {
  const router = useRouter();
  const isEditMode = Boolean(editId);
  const { data: estimateSettings, isLoading: estimateSettingsLoading } = useEstimateSettings();

  // Contact selection — seed from the service-call contact when prefilling.
  const [contactId, setContactId] = useState<string | null>(prefillContactId ?? null);

  // Customer
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [contactAddresses, setContactAddresses] = useState<ContactAddress[]>([]);

  // Addresses
  const [serviceAddressMode, setServiceAddressMode] = useState<'select' | 'manual'>('select');
  const [selectedServiceAddressIndex, setSelectedServiceAddressIndex] = useState<number | null>(
    null,
  );
  const [showAllServiceAddresses, setShowAllServiceAddresses] = useState(false);
  const [saStreet, setSaStreet] = useState('');
  const [saStreet2, setSaStreet2] = useState('');
  const [saCity, setSaCity] = useState('');
  const [saState, setSaState] = useState('');
  const [saZip, setSaZip] = useState('');
  // The country behind a typed address, which has no field of its own: kept
  // from a stored proposal or a source job's snapshot so it is not re-filed
  // as US on save. Saved rows carry their own.
  const [saCountry, setSaCountry] = useState('');
  const [billingMode, setBillingMode] = useState<'select' | 'manual'>('select');
  const [selectedBillingIndex, setSelectedBillingIndex] = useState<number | null>(null);
  const [showAllBillingAddresses, setShowAllBillingAddresses] = useState(false);
  const [baStreet, setBaStreet] = useState('');
  const [baStreet2, setBaStreet2] = useState('');
  const [baCity, setBaCity] = useState('');
  const [baState, setBaState] = useState('');
  const [baZip, setBaZip] = useState('');
  const [baCountry, setBaCountry] = useState('');
  const membershipServiceAddressId =
    serviceAddressMode === 'select' && selectedServiceAddressIndex !== null
      ? (contactAddresses[selectedServiceAddressIndex]?.id ?? null)
      : null;

  // Options
  const [options, setOptions] = useState<OptionState[]>([
    createEmptyProposalOption('Good'),
    createEmptyProposalOption('Better'),
    createEmptyProposalOption('Best'),
  ]);

  // Notes & terms
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);

  // Seed a NEW proposal's terms: the source Job's wording when
  // there is one, else the tenant default. The form always sends an explicit
  // selection, so leaving the selector blank would save terms the operator
  // never saw — the mismatch this whole series started from.
  // `isLoading` rather than the presence of `data`: a lookup that settles with
  // nothing — deleted job, no access, a stale link — must count as FINISHED, or
  // both the seed below and the pending copy would wait on it forever.
  // Silent: this dereference is OPTIONAL and a failure is already handled by
  // seeding the tenant default, so a stale or deleted service-call link should
  // not greet the operator with a job-fetch error toast on a page that went on
  // to do the right thing.
  const { data: sourceJob, isLoading: loadingSourceJob } = useJob(
    !isEditMode && sourceJobId ? sourceJobId : '',
    { showErrorToast: false },
  );
  const { data: proposalTermsTemplates, isLoading: loadingTermsTemplates } = useTermsTemplates();
  const didSeedTermsRef = useRef(false);
  // An update sends the terms block only when the operator changed it, so a
  // notes-only save keeps the server's fast path (no baseline row read, no
  // re-resolution of wording the customer has already seen).
  const termsTouchedRef = useRef(false);
  useEffect(() => {
    if (isEditMode || didSeedTermsRef.current) return;
    // Wait for the lookup to SETTLE, not to produce a job. When it comes back
    // empty we still seed — from the tenant default — which is exactly what the
    // server does with a source job it cannot resolve either.
    if (sourceJobId && loadingSourceJob) return;
    const seeded = seedDocumentTerms(sourceJob, proposalTermsTemplates ?? []);
    if (!seeded) return;
    didSeedTermsRef.current = true;
    setTerms((current) => (current.termsAndConditions.trim() ? current : seeded));
  }, [isEditMode, loadingSourceJob, proposalTermsTemplates, sourceJob, sourceJobId]);

  // "We don't know yet", as opposed to "we know, and it's nothing". Only the
  // second may render the empty state. Resolution is finished once the queries
  // it depends on have settled, whether or not they produced a selection —
  // otherwise a tenant with no default and no source job would sit in the
  // pending copy forever.
  const termsResolving =
    !isEditMode &&
    !didSeedTermsRef.current &&
    (loadingTermsTemplates || (!!sourceJobId && loadingSourceJob));

  const [internalNotes, setInternalNotes] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const [validUntil, setValidUntil] = useState('');

  const createProposal = useCreateProposal();
  const updateProposal = useUpdateProposal();
  const { data: existingProposal, isLoading: loadingProposal } = useProposal(editId ?? '');
  const { data: selectedTemplate } = useProposalTemplate(templateId ?? '');
  const { data: selectedContact } = useContact(contactId ?? '');
  // NOT-605 — an ACTIVE membership on the customer prices every option at member
  // rates. When there's none, a membership sold IN an option unlocks member rates
  // for that option's own siblings (join-&-save), so we also resolve the plans
  // referenced by any in-cart MEMBERSHIP line. Both feed the builder preview AND
  // the trusted create/update payload, per option.
  const {
    membershipInfo: activeMembershipInfo,
    isSettled: activeMembershipSettled,
    hasError: activeMembershipError,
    retry: retryActiveMembership,
  } = useEstimateMembershipInfo(contactId);

  const inCartMembershipPlanIds = useMemo(() => {
    const ids = new Set<string>();
    for (const option of options) {
      for (const item of option.items) {
        if (
          String(item.type ?? '').toUpperCase() === 'MEMBERSHIP' &&
          item.isSelected !== false &&
          item.membershipPlanId
        ) {
          ids.add(item.membershipPlanId);
        }
      }
    }
    return [...ids].sort();
  }, [options]);

  // Resolve every in-cart plan even for an active member: a distinct second plan
  // can add complementary benefits, and the pricing engine chooses the best
  // benefit per line without stacking.
  const {
    plansById: membershipPlansById,
    isSettled: membershipPlansSettled,
    hasError: membershipPlansError,
    retry: retryMembershipPlans,
  } = useMembershipPlansByIds(inCartMembershipPlanIds, {
    enabled: inCartMembershipPlanIds.length > 0,
  });

  const membershipPricingError = Boolean(
    (contactId && activeMembershipError) || membershipPlansError,
  );
  const membershipPricingSettled =
    (!contactId || activeMembershipSettled) && membershipPlansSettled;
  const retryMembershipPricing = useCallback(() => {
    if (contactId && activeMembershipError) retryActiveMembership();
    if (membershipPlansError) retryMembershipPlans();
  }, [
    activeMembershipError,
    contactId,
    membershipPlansError,
    retryActiveMembership,
    retryMembershipPlans,
  ]);

  useEffect(() => {
    if (membershipPricingError) {
      toast.error('Unable to load membership pricing. Try again before saving.');
    }
  }, [membershipPricingError]);

  const {
    attachments,
    setAttachments,
    pendingAttachmentId,
    excludedTemplateAttachmentIds,
    handleCustomerAttachmentsChange,
  } = useProposalAttachmentState({ isEditMode, editId, templateId });

  const applyServiceAddress = useCallback((address: ContactAddress) => {
    setSaStreet(address.street ?? '');
    setSaStreet2(address.street2 ?? '');
    setSaCity(address.city ?? '');
    setSaState((address.state ?? '').toUpperCase().slice(0, 2));
    setSaZip(sanitizePostalCodeInput(address.zip ?? ''));
    setSaCountry(address.country?.trim() ?? '');
  }, []);

  // Apply a picked/prefilled contact onto the customer + address fields. Shared
  // by the contact picker and the NOT-560 service-call prefill effect below,
  // which also passes the job so its site wins over the customer's default.
  const applyContactSelection = useCallback(
    (
      contact: ContactSelectionInput,
      sourceJobAddresses?: Parameters<typeof sourceJobAddressPicks>[1],
    ) => {
      const id = contact._id ?? contact.id ?? '';
      const name = getContactDisplayName(contact);
      const primaryAddress = getPrimaryContactAddress(contact.addresses);
      const nextAddresses = contact.addresses ?? [];
      const primaryServiceAddress =
        nextAddresses.find((address) => address.isService !== false) ?? null;
      const primaryBillingIndex = resolveProposalBillingAddressIndex(nextAddresses, null);
      const primaryBillingAddress =
        primaryBillingIndex !== null ? nextAddresses[primaryBillingIndex] : null;

      setContactId(id);
      setCustomerName(name);
      setCustomerEmail(contact.primaryEmail ?? '');
      setCustomerPhone(contact.primaryPhone ?? '');
      setCustomerAddress(formatContactAddress(primaryAddress));
      setContactAddresses(nextAddresses);

      if (primaryServiceAddress) {
        setServiceAddressMode('select');
        setSelectedServiceAddressIndex(nextAddresses.indexOf(primaryServiceAddress));
        applyServiceAddress(primaryServiceAddress);
      } else {
        setServiceAddressMode('manual');
        setSelectedServiceAddressIndex(null);
        setSaStreet('');
        setSaCountry('');
        setSaStreet2('');
        setSaCity('');
        setSaState('');
        setSaZip('');
      }

      if (primaryBillingAddress) {
        setBillingMode('select');
        // Not a billing CHOICE — the bill goes to the customer by default, and
        // pre-selecting a row here made the form open with "Bill somewhere
        // else" already on. `customerAddress` still carries the address for
        // display; `selectedBillingIndex` stays null until someone picks.
        setSelectedBillingIndex(null);
        setCustomerAddress(formatContactAddress(primaryBillingAddress));
        setBaStreet('');
        setBaCountry('');
        setBaStreet2('');
        setBaCity('');
        setBaState('');
        setBaZip('');
      } else {
        setBillingMode('manual');
        setSelectedBillingIndex(null);
        setBaStreet('');
        setBaCountry('');
        setBaStreet2('');
        setBaCity('');
        setBaState('');
        setBaZip('');
      }

      const picks = sourceJobAddressPicks(nextAddresses, sourceJobAddresses);
      if (picks.service?.kind === 'saved') {
        setServiceAddressMode('select');
        setSelectedServiceAddressIndex(picks.service.index);
        applyServiceAddress(nextAddresses[picks.service.index]);
      } else if (picks.service?.kind === 'manual') {
        setServiceAddressMode('manual');
        setSelectedServiceAddressIndex(null);
        applyServiceAddress(toContactAddress(picks.service.address));
      }
      if (picks.billing?.kind === 'saved') {
        setBillingMode('select');
        setSelectedBillingIndex(picks.billing.index);
        setCustomerAddress(formatContactAddress(nextAddresses[picks.billing.index]));
      } else if (picks.billing?.kind === 'manual') {
        const billing = toContactAddress(picks.billing.address);
        setBillingMode('manual');
        setSelectedBillingIndex(null);
        setBaStreet(billing.street ?? '');
        setBaStreet2(billing.street2 ?? '');
        setBaCity(billing.city ?? '');
        setBaState((billing.state ?? '').toUpperCase().slice(0, 2));
        setBaZip(sanitizePostalCodeInput(billing.zip ?? ''));
        setBaCountry(billing.country?.trim() ?? '');
      }
    },
    [applyServiceAddress],
  );

  // ── NOT-560: prefill the customer from the service-call contact ───────────
  const didPrefillContactRef = useRef(false);
  useEffect(() => {
    if (isEditMode || !prefillContactId || didPrefillContactRef.current) return;
    if (!selectedContact) return;
    // Raised on a job: wait for it, so the proposal is quoted for the job's
    // site rather than the customer's primary one.
    if (sourceJobId && loadingSourceJob) return;
    didPrefillContactRef.current = true;
    applyContactSelection(selectedContact, sourceJob);
  }, [
    isEditMode,
    prefillContactId,
    selectedContact,
    applyContactSelection,
    sourceJobId,
    loadingSourceJob,
    sourceJob,
  ]);

  useLocationPrefill({
    locationId: prefillLocationId,
    ownerId: prefillContactId,
    contactId,
    addresses: contactAddresses,
    enabled: !isEditMode && !sourceJobId,
    // contactId is seeded before the fetch; wait for its address rows to land.
    addressesReady: contactAddresses === selectedContact?.addresses,
    onUnavailable: () => {
      setSelectedServiceAddressIndex(null);
      setServiceAddressMode('manual');
      applyServiceAddress({});
    },
    onSelect: (index, address) => {
      setServiceAddressMode('select');
      setSelectedServiceAddressIndex(index);
      applyServiceAddress(address);
    },
  });

  // ── Populate form in edit mode ───────────────────────────────────────────
  const didPopulateRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !existingProposal || didPopulateRef.current) return;
    didPopulateRef.current = true;
    const p = existingProposal;

    const cust = p.customer;
    if (cust) {
      setCustomerName(cust.name ?? '');
      setCustomerEmail(cust.email ?? '');
      setCustomerPhone(cust.phone ?? '');
      setCustomerAddress(cust.address ?? '');
    }

    const cId = p.contactId ?? p.customer?.contactId ?? null;
    if (cId) setContactId(cId);

    const rawOptions = p.options ?? [];
    if (rawOptions.length > 0) {
      setOptions(hydrateProposalOptions(rawOptions as unknown as Record<string, unknown>[]));
    }

    const proposalServiceAddress = p.serviceAddress;
    if (proposalServiceAddress) {
      setServiceAddressMode('manual');
      setSelectedServiceAddressIndex(null);
      setSaStreet(proposalServiceAddress.street ?? '');
      setSaStreet2(proposalServiceAddress.street2 ?? '');
      setSaCity(proposalServiceAddress.city ?? '');
      setSaState((proposalServiceAddress.state ?? '').toUpperCase().slice(0, 2));
      setSaZip(sanitizePostalCodeInput(proposalServiceAddress.zip ?? ''));
      setSaCountry(proposalServiceAddress.country?.trim() ?? '');
    }

    const proposalBillingAddress = p.billingAddress;
    if (proposalBillingAddress) {
      setBillingMode('manual');
      setSelectedBillingIndex(null);
      setBaStreet(proposalBillingAddress.street ?? '');
      setBaStreet2(proposalBillingAddress.street2 ?? '');
      setBaCity(proposalBillingAddress.city ?? '');
      setBaState((proposalBillingAddress.state ?? '').toUpperCase().slice(0, 2));
      setBaZip(sanitizePostalCodeInput(proposalBillingAddress.zip ?? ''));
      setBaCountry(proposalBillingAddress.country?.trim() ?? '');
      setCustomerAddress(
        [
          proposalBillingAddress.street,
          proposalBillingAddress.street2,
          [proposalBillingAddress.city, proposalBillingAddress.state, proposalBillingAddress.zip]
            .filter(Boolean)
            .join(', '),
        ]
          .filter(Boolean)
          .join(', '),
      );
    }

    setValidUntil(p.validUntil ? String(p.validUntil).split('T')[0] : '');
    setNotes(p.notes ?? '');
    setTerms(
      readTermsSelection({
        termsMode: p.termsMode,
        termsTemplateIds: p.termsTemplateIds,
        termsAndConditions: p.terms,
      }),
    );
    setInternalNotes(p.internalNotes ?? '');
  }, [isEditMode, existingProposal]);

  useEffect(() => {
    if (!selectedContact?.addresses?.length) return;

    setContactAddresses(selectedContact.addresses);

    const proposal = existingProposal;

    if (proposal?.serviceAddress) {
      const serviceMatchIndex = findProposalServiceAddressIndex(
        selectedContact.addresses,
        proposal.serviceAddress,
      );

      if (serviceMatchIndex >= 0) {
        setServiceAddressMode('select');
        setSelectedServiceAddressIndex(serviceMatchIndex);
        applyServiceAddress(selectedContact.addresses[serviceMatchIndex]);
      }
    }

    if (proposal?.billingAddress) {
      const billingMatchIndex = findProposalBillingAddressIndex(
        selectedContact.addresses,
        proposal.billingAddress,
      );

      if (billingMatchIndex >= 0) {
        setBillingMode('select');
        setSelectedBillingIndex(billingMatchIndex);
        setCustomerAddress(formatContactAddress(selectedContact.addresses[billingMatchIndex]));
        setBaStreet('');
        setBaCountry('');
        setBaStreet2('');
        setBaCity('');
        setBaState('');
        setBaZip('');
      }
    }
  }, [selectedContact, existingProposal, applyServiceAddress]);

  // ── Populate from template ─────────────────────────────────────────────
  const appliedTemplateRef = useRef('');
  useEffect(() => {
    if (
      !selectedTemplate ||
      loadingTermsTemplates ||
      appliedTemplateRef.current === selectedTemplate.id
    )
      return;
    appliedTemplateRef.current = selectedTemplate.id;
    setNotes(selectedTemplate.notes ?? '');
    // Legacy templates may carry only the resolved text; current templates
    // carry the repository mode plus an ordered stack of ids. A template that
    // says nothing still leaves the proposal's inherited selection alone.
    const templateTerms = {
      termsMode: selectedTemplate.termsMode,
      termsTemplateIds: selectedTemplate.termsTemplateIds,
      termsAndConditions: selectedTemplate.terms,
    };
    if (hasTermsSelection(templateTerms)) {
      termsTouchedRef.current = true;
      setTerms(resolveTermsSelection(templateTerms, proposalTermsTemplates ?? []));
    }
    setInternalNotes(selectedTemplate.internalNotes ?? '');
    const tplOptions = selectedTemplate.options ?? [];
    if (tplOptions.length > 0) {
      setOptions(
        hydrateProposalOptions(
          tplOptions as unknown as Record<string, unknown>[],
          undefined,
          'new',
        ),
      );
    }
  }, [loadingTermsTemplates, proposalTermsTemplates, selectedTemplate]);

  const addOption = useCallback(() => {
    setOptions((prev) => {
      if (prev.length >= MAX_PROPOSAL_OPTIONS) return prev;
      return [...prev, createEmptyProposalOption(`Option ${prev.length + 1}`)];
    });
  }, []);

  const removeOption = useCallback((index: number) => {
    setOptions((prev) =>
      prev.length <= MIN_PROPOSAL_OPTIONS ? prev : prev.filter((_, i) => i !== index),
    );
  }, []);

  const updateOption = useCallback((index: number, updates: Partial<OptionState>) => {
    setOptions((prev) =>
      prev.map((option, idx) => {
        if (updates.recommended === true) {
          return idx === index ? { ...option, ...updates } : { ...option, recommended: false };
        }
        return idx === index ? { ...option, ...updates } : option;
      }),
    );
  }, []);

  const duplicateOption = useCallback((index: number) => {
    setOptions((prev) => {
      if (prev.length >= MAX_PROPOSAL_OPTIONS) return prev;
      const source = prev[index];
      if (!source) return prev;
      const copy: OptionState = {
        ...source,
        clientId: createProposalOptionClientId(),
        optionKey: undefined,
        legacyOptionIndex: undefined,
        // A copy holds nothing yet: the server judges its deposit as one this
        // save SETS, so the card must judge it the same way.
        storedDepositType: undefined,
        storedDepositValue: undefined,
        storedDepositRequired: undefined,
        label: `${source.label || `Option ${index + 1}`} copy`,
        recommended: false,
        expanded: true,
        items: source.items.map((item) => ({
          ...item,
          rowId: createUuid(),
          legacySourceIndex: undefined,
          displayConfig: item.displayConfig ? { ...item.displayConfig } : item.displayConfig,
          membershipPlanSummary: item.membershipPlanSummary
            ? {
                ...item.membershipPlanSummary,
                coveredServices: item.membershipPlanSummary.coveredServices.map((service) => ({
                  ...service,
                })),
              }
            : item.membershipPlanSummary,
        })),
      };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
  }, []);

  const reorderOptions = useCallback((nextOptions: OptionState[]) => {
    setOptions(nextOptions);
  }, []);

  const sendProposal = useSendProposal();
  // Which footer button the operator pressed, so only that one spins. Cleared
  // once the proposal exists and the channel modal takes over.
  const [activeAction, setActiveAction] = useState<'save' | 'send' | null>(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [pendingSendId, setPendingSendId] = useState<string | null>(null);
  const [approvalMethod, setApprovalMethod] = useState<ProposalSendApprovalMethod | ''>('');
  // Held for the WHOLE submit, not just the request. Save & Send keeps running
  // after the create resolves — modal, confirm, send, redirect — and the
  // mutation flags read idle for most of that. Released only on error, so a
  // failed attempt can retry; a successful one stays locked until unmount.
  const [flowPending, setFlowPending] = useState(false);
  // Synchronous twin of the flag: rejects a re-entrant click in the same tick,
  // before React can apply the state-driven disable.
  const flowRef = useRef(false);

  const isMutating = createProposal.isPending || updateProposal.isPending;
  const isResolvingMembershipPlans = !membershipPricingSettled && !membershipPricingError;
  const showInternal = internalOpen || Boolean(internalNotes.trim());
  const customLineItemsNamed = options.every((option) =>
    option.items.every((item) => item.code || item.name.trim()),
  );

  const scheduleGate = assessProposalPaymentSchedules(
    options.map((option) => {
      const preview = computeProposalOptionPreview(
        option,
        resolveOptionMembershipInfo(
          option,
          activeMembershipInfo ?? null,
          membershipPlansById ?? new Map(),
        ),
      );
      return {
        label: option.label,
        paymentScheduleTemplate: option.paymentScheduleTemplate,
        total: preview.total,
        depositRequired: option.depositRequired,
        depositAmount: proposalOptionDepositAmountOn(
          {
            depositType: option.depositType === '%' ? 'PERCENT' : 'FLAT',
            depositValue: option.depositValue,
          },
          preview.depositEligibleTotal,
        ),
      };
    }),
    estimateSettings?.paymentSchedule?.requireScheduleAboveAmount ?? 0,
    estimateSettings?.paymentSchedule?.allowPaymentSchedules,
  );

  const canSave =
    Boolean(contactId || customerName.trim()) &&
    options.length >= MIN_PROPOSAL_OPTIONS &&
    options.every((opt) => opt.label.trim()) &&
    options.every((opt) => opt.items.filter((i) => !i.isOptional).length > 0) &&
    customLineItemsNamed &&
    membershipPricingSettled &&
    !membershipPricingError &&
    !scheduleGate.invalidReason;

  // Saving and sending are not the same gate. `POST /proposals/:id/send`
  // refuses a customer with neither an email nor a phone, and SendConfirmModal
  // derives its channels from the same two fields — so without one the modal
  // opens with nothing selectable and Confirm dead. Left ungated, "Save & Send"
  // writes the draft first and only then reveals it can only save.
  const sendBlockedReason =
    !customerEmail.trim() && !customerPhone.trim()
      ? SEND_BLOCKED_REASON
      : estimateSettingsLoading
        ? 'Waiting for payment schedule settings before sending.'
        : scheduleGate.missingRequiredReason;
  const canSend = !sendBlockedReason;

  const handleSubmit = useCallback(
    async (action: 'save' | 'send' = 'save') => {
      if (membershipPricingError) {
        toast.error('Membership pricing is unavailable. Retry before saving.');
        return;
      }
      if (!membershipPricingSettled) {
        toast.error('Wait for membership pricing to finish loading before saving.');
        return;
      }
      if (!contactId && !customerName.trim()) {
        toast.error('Please select a contact');
        return;
      }
      if (options.length < MIN_PROPOSAL_OPTIONS) {
        toast.error(`A proposal needs at least ${MIN_PROPOSAL_OPTIONS} options`);
        return;
      }
      const unnamedIdx = options.findIndex((opt) => !opt.label.trim());
      if (unnamedIdx !== -1) {
        toast.error(`Option ${unnamedIdx + 1} needs a name`);
        return;
      }
      const emptyIdx = options.findIndex(
        (opt) => opt.items.filter((i) => !i.isOptional).length === 0,
      );
      if (emptyIdx !== -1) {
        toast.error(`Option "${options[emptyIdx].label}" needs at least one required item`);
        return;
      }
      const unnamedCustomOption = options.find((option) =>
        option.items.some((item) => !item.code && !item.name.trim()),
      );
      if (unnamedCustomOption) {
        toast.error(`Name each custom item in "${unnamedCustomOption.label}"`);
        return;
      }
      // The option cards already say this inline; the gate keeps a figure the
      // server refuses from reaching it as an opaque 400.
      const boundError = findProposalOptionBoundError(
        options,
        activeMembershipInfo ?? null,
        membershipPlansById ?? new Map(),
      );
      if (boundError) {
        toast.error(boundError);
        return;
      }

      if (scheduleGate.invalidReason) {
        toast.error(scheduleGate.invalidReason);
        return;
      }

      const payload = buildProposalPayload({
        // Send a terms selection only when we actually have one: the operator
        // touched the selector, or seeding completed. Submitting during the
        // templates-loading window would otherwise send a blank DEFAULT that
        // OVERRIDES the server's own inheritance — the form imposing an opinion
        // it does not have. With the block omitted the server resolves from the
        // source job / tenant default, which is what the selector would have
        // shown a moment later.
        includeTerms: termsTouchedRef.current || (!isEditMode && didSeedTermsRef.current),
        includeIdentityClears: isEditMode,
        contactId,
        customerName,
        customerEmail,
        customerPhone,
        customerAddress,
        serviceAddressMode,
        selectedServiceAddressIndex,
        billingMode,
        selectedBillingIndex,
        contactAddresses,
        saStreet,
        saStreet2,
        saCity,
        saState,
        saZip,
        saCountry,
        baStreet,
        baStreet2,
        baCity,
        baState,
        baZip,
        baCountry,
        validUntil,
        options,
        notes,
        terms,
        internalNotes,
        pendingAttachmentId,
        includePendingAttachmentId: !isEditMode && attachments.length > 0,
        // NOT-560 — only new (non-edit) proposals carry the service-call link.
        sourceJobId: isEditMode ? undefined : sourceJobId,
        // NOT-605 — member rates: active membership (all options) or a plan sold in
        // an option (that option's siblings), resolved per option in the payload.
        activeMembershipInfo,
        membershipPlansById,
      });

      // The disabled button is a courtesy, not the rule. Any caller reaching the
      // send path directly would otherwise write the draft and then open a modal
      // with no selectable channel — the dead end this refuses.
      if (action === 'send' && !canSend) {
        toast.error(sendBlockedReason ?? SEND_BLOCKED_REASON);
        return;
      }

      // Committed: every validation above has passed and a request is about to
      // go out. Taken here rather than in the click handler so a rejected
      // attempt never leaves the form locked.
      if (flowRef.current) return;
      flowRef.current = true;
      setFlowPending(true);
      const releaseFlow = () => {
        flowRef.current = false;
        setFlowPending(false);
      };

      if (isEditMode && editId) {
        updateProposal.mutate(
          {
            id: editId,
            dto: payload as unknown as import('@/types/proposal').UpdateProposalDto,
          },
          {
            onSuccess: (data: unknown) => {
              const r = data as { _id?: string; id?: string };
              void router.navigate({
                to: '/dashboard/proposals/$id',
                params: { id: r._id ?? r.id ?? editId },
                search: {} as never,
              });
            },
            onError: releaseFlow,
          },
        );
      } else {
        createProposal.mutate(payload as unknown as import('@/types/proposal').CreateProposalDto, {
          onSuccess: async (result: unknown) => {
            const r = result as { _id?: string; id?: string };
            const proposalId = r._id ?? r.id ?? '';
            if (templateId) {
              try {
                await apiPost(
                  operationsClient,
                  proposalTemplateEndpoints.copyAttachments(templateId),
                  { proposalId, excludeIds: excludedTemplateAttachmentIds },
                );
              } catch {
                // non-blocking
              }
            }
            // Save & Send is two requests, like the estimate flow: the draft now
            // exists, so hand the operator the channel picker instead of
            // navigating. Sending happens on confirm.
            if (action === 'send' && proposalId) {
              setPendingSendId(proposalId);
              setShowSendConfirm(true);
              return;
            }
            void router.navigate({
              to: '/dashboard/proposals/$id',
              params: { id: proposalId },
              search: {} as never,
            });
          },
          onError: releaseFlow,
        });
      }
    },
    [
      contactId,
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      serviceAddressMode,
      selectedServiceAddressIndex,
      billingMode,
      selectedBillingIndex,
      contactAddresses,
      saStreet,
      saStreet2,
      saCity,
      saState,
      saZip,
      saCountry,
      baStreet,
      baStreet2,
      baCity,
      baState,
      baZip,
      baCountry,
      validUntil,
      options,
      activeMembershipInfo,
      membershipPlansById,
      membershipPricingError,
      membershipPricingSettled,
      notes,
      terms,
      internalNotes,
      pendingAttachmentId,
      attachments.length,
      excludedTemplateAttachmentIds,
      isEditMode,
      editId,
      templateId,
      sourceJobId,
      canSend,
      sendBlockedReason,
      scheduleGate.invalidReason,
      createProposal,
      updateProposal,
      router,
    ],
  );

  // The draft exists from here on, so every exit from the modal goes to the
  // proposal. Returning to the form would leave a saved proposal behind an
  // editable create form, and a second submit would make a duplicate.
  const goToPendingProposal = useCallback(
    (id: string) => {
      void router.navigate({
        to: '/dashboard/proposals/$id',
        params: { id },
        search: {} as never,
      });
    },
    [router],
  );

  const handleSendConfirm = useCallback(
    (channels: ('email' | 'sms')[]) => {
      const id = pendingSendId;
      setShowSendConfirm(false);
      setPendingSendId(null);
      if (!id) return;
      sendProposal.mutate(
        { id, channels, approvalMethod: approvalMethod || null },
        // A customer with no email is a 400 from the send endpoint. The
        // proposal is saved either way, so both outcomes land on it rather
        // than stranding the operator on a form that would create a second.
        { onSuccess: () => goToPendingProposal(id), onError: () => goToPendingProposal(id) },
      );
    },
    [pendingSendId, approvalMethod, sendProposal, goToPendingProposal],
  );

  const handleSendCancel = useCallback(() => {
    const id = pendingSendId;
    setShowSendConfirm(false);
    setPendingSendId(null);
    if (id) goToPendingProposal(id);
  }, [pendingSendId, goToPendingProposal]);

  // Every term of `canSave` above gets a sentence, so a greyed-out button is
  // never unexplained — and the send-only reason is never left to fill a
  // silence it does not belong in.
  const blockedReason =
    proposalSaveBlockedReason({
      hasCustomer: Boolean(contactId || customerName.trim()),
      optionCount: options.length,
      everyOptionNamed: options.every((opt) => opt.label.trim()),
      everyOptionHasRequiredItem: options.every(
        (opt) => opt.items.filter((i) => !i.isOptional).length > 0,
      ),
      everyCustomItemNamed: customLineItemsNamed,
      membershipPricingError,
      membershipPricingSettled: !isResolvingMembershipPlans,
    }) ?? scheduleGate.invalidReason;

  if (isEditMode && loadingProposal) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <FormPageLayout
      title={isEditMode ? 'Edit proposal' : 'New proposal'}
      subtitle="Offer good / better / best — the customer picks one."
      onBack={() => router.history.back()}
      actions={
        <ProposalFormActions
          isEditMode={isEditMode}
          submitting={proposalSubmitLocked({
            creating: createProposal.isPending,
            updating: updateProposal.isPending,
            sending: sendProposal.isPending,
            flowPending,
          })}
          savePending={isMutating && activeAction !== 'send'}
          // Spins for the create AND the send, but not for the stretch in
          // between — the modal is up then, and a spinner behind it reads as
          // work the operator is still being asked to authorise.
          sendPending={(isMutating || sendProposal.isPending) && activeAction === 'send'}
          canSave={canSave}
          blockedReason={blockedReason}
          canSend={canSend}
          sendBlockedReason={sendBlockedReason}
          onRetryBlocked={membershipPricingError ? retryMembershipPricing : undefined}
          onCancel={() => router.history.back()}
          onSave={() => {
            setActiveAction('save');
            void handleSubmit('save').catch(() => {});
          }}
          onSaveAndSend={() => {
            setActiveAction('send');
            void handleSubmit('send').catch(() => {});
          }}
        />
      }
    >
      <div className="min-h-full bg-muted/40">
        <div className="mx-auto max-w-6xl space-y-3 px-4 py-5">
          {/* No `overflow-hidden` here, unlike its sibling cards: the customer
              search drops an absolutely-positioned list below the field, and a
              clipping ancestor cuts it off at the card edge. */}
          <FormSectionCard className="rounded-lg border bg-card p-0 shadow-none">
            <div className="border-b px-4 py-3">
              <FormSectionHeader
                icon={Users}
                title="Customer"
                description="Who this proposal is for and where work happens."
              />
            </div>
            <div className="px-4 py-4">
              <JobCustomerContactsSection
                open
                bare
                compactWhenSelected
                showServiceContact={false}
                onToggle={() => undefined}
                contactId={contactId}
                customerName={customerName}
                selectedCustomer={
                  contactId
                    ? {
                        id: contactId,
                        name: customerName,
                        primaryEmail: customerEmail || undefined,
                        primaryPhone: customerPhone || undefined,
                        address: customerAddress || undefined,
                      }
                    : null
                }
                customerEmail={customerEmail}
                customerPhone={customerPhone}
                onEmailChange={setCustomerEmail}
                onPhoneChange={setCustomerPhone}
                onSelectContact={applyContactSelection}
                onClearContact={() => {
                  setContactId(null);
                  setCustomerName('');
                  setCustomerEmail('');
                  setCustomerPhone('');
                  setCustomerAddress('');
                  setContactAddresses([]);
                  setServiceAddressMode('select');
                  setBillingMode('select');
                  setSelectedServiceAddressIndex(null);
                  setSelectedBillingIndex(null);
                  setSaStreet('');
                  setSaCountry('');
                  setSaStreet2('');
                  setSaCity('');
                  setSaState('');
                  setSaZip('');
                  setBaStreet('');
                  setBaCountry('');
                  setBaStreet2('');
                  setBaCity('');
                  setBaState('');
                  setBaZip('');
                }}
                contactAddresses={contactAddresses}
                addressMode={serviceAddressMode}
                selectedAddressIndex={selectedServiceAddressIndex}
                showAllAddresses={showAllServiceAddresses}
                onToggleAddressMode={() => {
                  if (serviceAddressMode === 'manual') {
                    const nextIndex = resolveProposalServiceAddressIndex(
                      contactAddresses,
                      selectedServiceAddressIndex,
                    );
                    const nextAddress = nextIndex !== null ? contactAddresses[nextIndex] : null;

                    setServiceAddressMode('select');
                    setSelectedServiceAddressIndex(nextIndex);
                    if (nextAddress) {
                      applyServiceAddress(nextAddress);
                    } else {
                      setSaStreet('');
                      setSaCountry('');
                      setSaStreet2('');
                      setSaCity('');
                      setSaState('');
                      setSaZip('');
                    }
                    return;
                  }
                  setServiceAddressMode('manual');
                }}
                onSelectServiceAddress={(index, address) => {
                  setSelectedServiceAddressIndex(index);
                  // The row itself, not the picker's projection of it, so the
                  // fields carry its country if the user switches to typing.
                  applyServiceAddress(contactAddresses[index] ?? address);
                }}
                onToggleShowAllAddresses={() => setShowAllServiceAddresses((current) => !current)}
                saStreet={saStreet}
                saStreet2={saStreet2}
                saCity={saCity}
                saState={saState}
                saZip={saZip}
                onSaStreetChange={setSaStreet}
                onSaStreet2Change={setSaStreet2}
                onSaCityChange={setSaCity}
                onSaStateChange={(value) => setSaState(value.toUpperCase().slice(0, 2))}
                onSaZipChange={(value) => setSaZip(sanitizePostalCodeInput(value))}
                selectedBillingIndex={selectedBillingIndex}
                showAllBillingAddresses={showAllBillingAddresses}
                onToggleBillingMode={() => {
                  if (billingMode === 'manual') {
                    const nextIndex = resolveProposalBillingAddressIndex(
                      contactAddresses,
                      selectedBillingIndex,
                    );
                    const nextAddress = nextIndex !== null ? contactAddresses[nextIndex] : null;

                    setBillingMode('select');
                    setSelectedBillingIndex(nextIndex);
                    setCustomerAddress(nextAddress ? formatContactAddress(nextAddress) : '');
                    setBaStreet('');
                    setBaCountry('');
                    setBaStreet2('');
                    setBaCity('');
                    setBaState('');
                    setBaZip('');
                    return;
                  }
                  setBillingMode('manual');
                  setCustomerAddress('');
                }}
                onResetBillingToService={() => {
                  setBillingMode('select');
                  setSelectedBillingIndex(null);
                  setCustomerAddress('');
                  setBaStreet('');
                  setBaCountry('');
                  setBaStreet2('');
                  setBaCity('');
                  setBaState('');
                  setBaZip('');
                }}
                onSelectBillingAddress={(index, address) => {
                  setSelectedBillingIndex(index);
                  setCustomerAddress(formatContactAddress(address));
                  setBaStreet('');
                  setBaCountry('');
                  setBaStreet2('');
                  setBaCity('');
                  setBaState('');
                  setBaZip('');
                }}
                onToggleShowAllBillingAddresses={() =>
                  setShowAllBillingAddresses((current) => !current)
                }
                billingMode={billingMode}
                baStreet={baStreet}
                baStreet2={baStreet2}
                baCity={baCity}
                baState={baState}
                baZip={baZip}
                onBaStreetChange={setBaStreet}
                onBaStreet2Change={setBaStreet2}
                onBaCityChange={setBaCity}
                onBaStateChange={(value) => setBaState(value.toUpperCase().slice(0, 2))}
                onBaZipChange={(value) => setBaZip(sanitizePostalCodeInput(value))}
              />
            </div>
          </FormSectionCard>

          <ProposalOptionsSection
            options={options}
            maxOptions={MAX_PROPOSAL_OPTIONS}
            onAddOption={addOption}
            onUpdateOption={updateOption}
            onDuplicateOption={duplicateOption}
            onRemoveOption={removeOption}
            onReorderOptions={reorderOptions}
            activeMembershipInfo={activeMembershipInfo}
            membershipPlansById={membershipPlansById}
            membershipCustomerId={contactId}
            membershipServiceAddressId={membershipServiceAddressId}
            businessUnitId={selectedBusinessUnitId}
          />

          <ProposalDetailsSection validUntil={validUntil} onValidUntilChange={setValidUntil} />

          <FormSectionCard className="overflow-hidden rounded-lg border bg-card p-0 shadow-none">
            <div className="border-b px-4 py-3">
              <FormSectionHeader
                icon={StickyNote}
                title="Notes and attachments"
                description="Visible on the customer proposal document."
              />
            </div>
            <div className="px-4 py-4">
              <EstimateCustomerContentSection
                open
                bare
                onToggle={() => undefined}
                entityType="proposal"
                notes={notes}
                onNotesChange={setNotes}
                isEditMode={isEditMode}
                editId={editId}
                pendingAttachmentId={pendingAttachmentId}
                attachments={attachments}
                onAttachmentsChange={
                  !isEditMode && templateId ? handleCustomerAttachmentsChange : setAttachments
                }
              />
            </div>
          </FormSectionCard>

          <FormSectionCard className="overflow-hidden rounded-lg border bg-card p-0 shadow-none">
            <div className="border-b px-4 py-3">
              <FormSectionHeader
                icon={ScrollText}
                title="Terms & conditions"
                description="Reusable legal and approval terms."
              />
            </div>
            <div className="px-4 py-4">
              <TermsSelector
                value={terms}
                // Until the source job and the terms repository have landed we
                // do not yet know what this document inherits, and the submit
                // path deliberately omits the terms block in that window so the
                // SERVER applies the inheritance. Saying "no terms will be
                // shown" here would therefore be telling the operator the
                // opposite of what saving now would do.
                resolving={termsResolving}
                onChange={(next) => {
                  termsTouchedRef.current = true;
                  setTerms(next);
                }}
              />
            </div>
          </FormSectionCard>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 px-1">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Add if needed
              </span>
              <AddChip
                active={showInternal}
                icon={<Lock className="h-3.5 w-3.5" />}
                label="Internal note"
                onClick={() => setInternalOpen((open) => !open)}
              />
            </div>

            {showInternal ? (
              <SituationalPanel title="Internal note" icon={<Lock className="h-4 w-4" />}>
                <Textarea
                  rows={4}
                  maxLength={5000}
                  value={internalNotes}
                  onChange={(event) => setInternalNotes(event.target.value)}
                  placeholder="Visible only to your team..."
                />
              </SituationalPanel>
            ) : null}
          </div>
        </div>
      </div>
      {/* The same channel picker the proposal detail page uses for Send. */}
      <SendConfirmModal
        open={showSendConfirm}
        title="Send Proposal"
        customerName={customerName}
        customerEmail={customerEmail || undefined}
        customerPhone={customerPhone || undefined}
        confirmLabel="Send"
        approvalMethod={approvalMethod}
        onApprovalMethodChange={(value) =>
          setApprovalMethod(value === 'SIGNATURE' || value === 'ONLINE' ? value : '')
        }
        approvalMethodOptions={PROPOSAL_APPROVAL_METHODS}
        isPending={sendProposal.isPending}
        onConfirm={handleSendConfirm}
        onCancel={handleSendCancel}
      />
    </FormPageLayout>
  );
}
